import useSWR from "swr";
import { customFetcher, getTsDate, notify } from "../../../utils";
import { ICorpStation, SelectOption, UnitIp } from "../../../globals/interfaces";
import Select from "../../../components/Select";
import { useState } from "react";
import { Label } from "reactstrap";
import styled from "styled-components";
import VirtualTable from "../../../components/custom/VirtualTable";
import ActionIcon from "../../../components/custom/ActionIcon";
import ModalManageIp from "./ModalManageIp";
import useModal from "../../../customHooks/useModal";
import apiClient from "../../../apiClient";
import useUser from "../../../customHooks/useUser";
import { create } from "lodash";
import { Button } from "reactstrap";

export type HandleCreateUpdateIp = (data: UnitIp) => Promise<void>;

export default function ManageUnitIp({
    activeTab
}) {

    const { user } = useUser();
    const stationsKey = activeTab === 'manageUnitIp' ? '/selectStations' : null;
    const {
        data: stations
    } = useSWR(stationsKey, customFetcher<ICorpStation>, {
        fallbackData: []
    });
    const stationOptions: Array<SelectOption> = stations.map(item => ({ label: item.s_unit, value: item.s_unit }));
    const [unit, setUnit] = useState('');

    const key = (activeTab === 'manageUnitIp' && Boolean(unit)) ? `/dev/unit-ip/${unit}` : null;
    const {
        data,
        mutate
    } = useSWR(key, customFetcher<UnitIp>, {
        fallbackData: []
    });

    const [id, setId] = useState<number>();
    const selectedIp = data.find(item => item.id === id);
    const { modal, setModal } = useModal();

    const handleManageIp = (id?: number) => {
        setId(id || undefined);
        setModal(true);
    }

    const handleCreateUpdateIp = async (data: UnitIp) => {
        const update = Boolean(id && selectedIp);
        const method = update ? 'put' : 'post';
        try {
            await apiClient[method](`/dev/unit-ip`, data);
            mutate();
            setModal(false);
            notify(update ? 'Updated' : 'Created');
        } catch (err) {
            alert(err);
        }
    }

    const deleteIpAddress = async () => {
        if (!selectedIp) return;

        try {
            await apiClient.delete(`/dev/unit-ip/${id}`);
            setModal(false);
            mutate();
            notify('Deleted');
        } catch (err) {
            alert(err);
        }
    }

    const propogateUnitIps = async () => {
        if (!unit && !data.length) return;

        const ips = data.map(item => (item.ipAddress)).join(',');

        try {
            await apiClient.put(`/dev/propogate-unit-ips`, {
                ips,
                unit
            });
            setModal(false);
            mutate();
            notify('Updated');
        } catch (err) {
            alert(err);
        }
    }

    return (
        <div>
            <SelectUnitContainer>
                <h6>Select Unit:</h6>
                <Select
                    options={stationOptions}
                    value={unit}
                    onChange={option => setUnit(String(option.value))}
                    width={150}
                />
            </SelectUnitContainer>
            {
                unit && (
                    <div className={'mt-3'}>
                        <FlexContainer>
                            <h6 className={''}>{unit} IP Addresses</h6>
                            <ActionIcon
                                type={'add'}
                                onClick={() => handleManageIp()}
                            />
                        </FlexContainer>
                        {
                            data.length !== 0 && (
                                <ActionIcon
                                    type={'push'}
                                    onClick={() => propogateUnitIps()}
                                    confirm={true}
                                    text={'Update entire unit'}
                                />
                            )
                        }
                        <VirtualTable<UnitIp>
                            data={data}
                            mapping={[{
                                name: 'IP Address',
                                value: 'ipAddress'
                            }, {
                                name: 'Notes',
                                value: 'notes'
                            }, {
                                name: 'Created By',
                                value: 'createdBy',
                                email: true
                            }, {
                                name: 'Created At',
                                value: 'createdAt',
                                datetime: true,
                                utc: true
                            }, {
                                name: 'Modified By',
                                value: 'modifiedBy',
                                email: true
                            }, {
                                name: 'Modified At',
                                value: 'modifiedAt',
                                datetime: true,
                                utc: true
                            }]}
                            numRows={10}
                            handleClick={item => handleManageIp(item.id)}
                        />
                    </div>
                )
            }
            <ModalManageIp
                modal={modal}
                setModal={setModal}
                selectedIp={selectedIp}
                handleCreateUpdateIp={handleCreateUpdateIp}
                deleteIpAddress={deleteIpAddress}
                unit={unit}
                user={user}
            />
        </div>
    );
}

const SelectUnitContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;